import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, acceptInvite, qk } from '../api.js';
import { PASSWORD_MIN_LENGTH, passwordProblem } from '../lib/auth.js';
import GirisKabugu, {
  AnimateIn,
  AlanEtiketi,
  HataSeridi,
  KartAciklama,
  KartBaslik,
  MarkaButonu,
  MetinAlani,
  ParolaAlani,
} from '../components/GirisKabugu.js';

/**
 * §27.7 — `/davet`: davet e-postasındaki linkin hedefi (`BASE_URL/davet?token=…`, §27.4).
 * Token'ı query'den okur, parola + ad-soyad ister, `POST /api/auth/accept` çağırır;
 * 410'da "davet süresi dolmuş" kartı gösterir.
 *
 * GÖRSEL DİL `GirisKabugu` ile referans admin paneline taşındı; DAVRANIŞ, tüm
 * `data-testid`ler ve metinler önceki sürümle birebir aynıdır.
 *
 * Sayfa route guard'ın DIŞINDADIR (§27.5 muafiyet listesi: `POST /api/auth/accept`):
 * davet edilen kişinin HENÜZ oturumu yoktur, guard'ın arkasında olsaydı linke tıklayan
 * herkes giriş sayfasına düşerdi.
 *
 * Kabul başarılıysa sunucu otomatik login eder (Set-Cookie, §27.4) — panel ek bir
 * `login` çağrısı YAPMAZ, doğrudan ana sayfaya geçer.
 */

/**
 * §27.4 accept hata sözleşmesi:
 *   410 — süresi geçmiş VEYA zaten kabul edilmiş token (ayrı kart, aşağıda)
 *   400 — politika dışı parola (`weak_password`) / eksik alan
 * Diğer durumlarda sunucunun kendi mesajı gösterilir.
 */
function acceptErrorText(error: unknown): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    if (error.status === 400) {
      return error.body.includes('weak_password')
        ? `Parola politikaya uymuyor: en az ${PASSWORD_MIN_LENGTH} karakter, 1 harf ve 1 rakam.`
        : error.message;
    }
    if (error.status === 0) return 'Sunucuya ulaşılamadı — bağlantınızı kontrol edin.';
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  // §27.4 — link biçimi `?token=<uuid>`; token yoksa kabul edilecek bir davet de yoktur.
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  // Sunucuya gitmeden önce politikayı söylemek için; asıl karar §27.3 ile sunucudadır.
  const [localProblem, setLocalProblem] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () => acceptInvite({ token, password, fullName: fullName.trim() }),
    onSuccess: async () => {
      // Otomatik login olduk: kimlik sorgusu tazelensin, panel yeni oturumla açılsın.
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: qk.me() });
      navigate('/', { replace: true });
    },
  });

  const status = accept.error instanceof ApiError ? accept.error.status : undefined;

  /*
   * §27.7 — 410 kartı. "Süresi dolmuş" ile "zaten kullanılmış" sunucuda AYNI koddur
   * (§27.4), bu yüzden metin iki olasılığı da kapsar ve tek çıkış yolunu gösterir:
   * proje sahibinden yeni davet istemek (yeni davet eskisini siler, §27.4 kararı).
   */
  if (status === 410) {
    return (
      <GirisKabugu testId="davet-sayfasi">
        <div data-testid="davet-suresi-dolmus" className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Davet süresi dolmuş</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60}>
            <KartAciklama>
              Bu davet bağlantısı artık geçerli değil (süresi doldu ya da daha önce
              kullanıldı). Proje sahibinden yeni bir davet isteyin.
            </KartAciklama>
          </AnimateIn>
        </div>
      </GirisKabugu>
    );
  }

  if (token === '') {
    return (
      <GirisKabugu testId="davet-sayfasi">
        <div className="flex flex-col gap-6">
          <AnimateIn delay={0}>
            <KartBaslik>Bağlantı eksik</KartBaslik>
          </AnimateIn>
          <AnimateIn delay={60}>
            <KartAciklama>
              Davet bağlantısı token içermiyor. E-postadaki adresi tarayıcıya olduğu gibi
              yapıştırdığınızdan emin olun.
            </KartAciklama>
          </AnimateIn>
        </div>
      </GirisKabugu>
    );
  }

  const serverError = acceptErrorText(accept.error);
  const error = localProblem ?? serverError;
  const disabled = password === '' || accept.isPending;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (disabled) return;
    const problem = passwordProblem(password);
    setLocalProblem(problem);
    if (problem !== null) return;
    accept.mutate();
  }

  return (
    <GirisKabugu testId="davet-sayfasi">
      <form className="flex flex-col gap-6" onSubmit={onSubmit} noValidate>
        <AnimateIn delay={0} className="flex flex-col gap-1.5">
          <KartBaslik>Daveti kabul et</KartBaslik>
          <KartAciklama>Hesabınızı oluşturmak için bir parola belirleyin.</KartAciklama>
        </AnimateIn>

        <AnimateIn delay={60} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <AlanEtiketi>Ad soyad</AlanEtiketi>
            <MetinAlani
              data-testid="davet-ad"
              type="text"
              name="fullName"
              autoComplete="name"
              autoFocus
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Ayşe Yılmaz"
              maxLength={120}
            />
          </div>

          <div className="flex flex-col gap-1">
            <AlanEtiketi>Parola</AlanEtiketi>
            <ParolaAlani
              data-testid="davet-parola"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                // Kullanıcı yazmaya devam ederken eski uyarı ekranda kalmasın.
                if (localProblem !== null) setLocalProblem(null);
              }}
              placeholder="••••••••••"
            />
            {/* §27.3 politikası önden söylenir — kullanıcı 400 alarak öğrenmemeli. */}
            <span className="font-grotesk text-[11px] text-[#686868]">
              En az {PASSWORD_MIN_LENGTH} karakter, en az 1 harf ve 1 rakam.
            </span>
          </div>

          {error !== null ? <HataSeridi testId="davet-hata">{error}</HataSeridi> : null}

          <MarkaButonu type="submit" data-testid="davet-gonder" disabled={disabled}>
            {accept.isPending ? 'Hesap oluşturuluyor…' : 'Daveti kabul et'}
          </MarkaButonu>
        </AnimateIn>
      </form>
    </GirisKabugu>
  );
}
